import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getActivePractices, checkContactPatient, getProviders } from '../services/patientService';

const BASE_REASON_OPTIONS = [
  'Appointment Request',
  'General Inquiry',
  'Insurance Question',
  'Other'
];

const REASON_DESCRIPTIONS = {
  'Appointment Request': 'Schedule a new appointment with a provider at this practice.',
  'General Inquiry': 'Have a question but not quite ready for an appointment yet? We\'re happy to help.',
  'Insurance Question': 'Ask whether a procedure or service may be covered by your insurance.',
  'Other': 'Something else not listed above — describe it in your message.',
  'Schedule a Follow-up Appointment': 'Book a follow-up visit related to a previous appointment or procedure.',
  'Billing Question': 'Question about a bill, statement, or payment for services received.',
  'Medical Records Request': 'Request a copy of your medical records or have them transferred.',
  'Prescription Refill': 'Request a refill for an existing prescription from your provider.',
  'Test Results': 'Inquire about lab work, imaging, or other test results.'
};

const ESTABLISHED_ONLY_OPTIONS = [
  'Schedule a Follow-up Appointment',
  'Billing Question',
  'Medical Records Request',
  'Prescription Refill',
  'Test Results'
];

const formatPhone = (value) => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};
export default function ContactForm() {
  const [practices, setPractices] = useState([]);
  const [practicesLoading, setPracticesLoading] = useState(true);
  const [lockedPractice, setLockedPractice] = useState(null);

  const [practice, setPractice] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [cellPhone, setCellPhone] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [preferredProvider, setPreferredProvider] = useState('');
  const [message, setMessage] = useState('');

  const [isEstablishedPatient, setIsEstablishedPatient] = useState(null); // null=unchecked, true=established, false=not found
  const [isCheckingPatient, setIsCheckingPatient] = useState(false);
  const checkKeyRef = useRef(0);

  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');

  // Check if form is embedded (hide header and practice dropdown)
  const params = new URLSearchParams(window.location.search);
  const isEmbedded = params.get('embedded') === 'true';
  
  // Track source of form completion (website, instagram, etc.)
  const [source, setSource] = useState('');
  
  // Track practice-specific color for branding
  const [practiceColor, setPracticeColor] = useState('#1976d2'); // Default blue

  useEffect(() => {
    const loadPractices = async () => {
      try {
        const data = await getActivePractices();
        const list = data || [];
        setPractices(list);

        // Check for ?practice=CODE and ?source in URL
        const params = new URLSearchParams(window.location.search);
        const code = params.get('practice');
        const sourceParam = params.get('source');
        
        // Set source from URL parameter
        if (sourceParam) {
          setSource(sourceParam);
        }
        
        if (code) {
          const normalized = String(code).toUpperCase();
          const match = list.find(
            (p) => (p.practice_code || '').toUpperCase() === normalized
          );
          if (match) {
            setPractice(match.practice_name);
            setLockedPractice(match.practice_name);
            setPracticeColor(match.color_main || '#1976d2');
          } else {
            // Fallback map for known codes
            const fallbackMap = {
              SAGS: 'San Antonio General Surgery',
              STARS: 'STARS Plastic Surgery'
            };
            if (fallbackMap[normalized]) {
              setPractice(fallbackMap[normalized]);
              setLockedPractice(fallbackMap[normalized]);
              // Find color for fallback practice
              const fallbackMatch = list.find(p => p.practice_name === fallbackMap[normalized]);
              setPracticeColor(fallbackMatch?.color_main || '#1976d2');
            }
          }
        }
      } catch (err) {
        console.error('Error loading practices:', err);
        setPractices([{ practice_name: 'STARS Plastic Surgery', practice_code: 'STARS' }]);
      } finally {
        setPracticesLoading(false);
      }
    };
    loadPractices();
  }, []);

  // Single effect: re-runs whenever any identifying field changes.
  // Uses a ref-based key so practice/name changes always trigger a fresh lookup.
  useEffect(() => {
    const phoneComplete = cellPhone.replace(/\D/g, '').length >= 10;
    const emailValid = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email);
    const canCheck = practice && firstName.trim() && lastName.trim() && (phoneComplete || emailValid);

    if (!canCheck) {
      setIsEstablishedPatient(null);
      return;
    }

    // Capture the current key; if it changes before the timeout fires we bail out
    const currentKey = ++checkKeyRef.current;
    setIsEstablishedPatient(null);

    const timer = setTimeout(async () => {
      if (checkKeyRef.current !== currentKey) return; // stale — a newer check is pending
      setIsCheckingPatient(true);
      try {
        const result = await checkContactPatient(
          practice,
          firstName.trim(),
          lastName.trim(),
          phoneComplete ? cellPhone.replace(/\D/g, '') : '',
          emailValid ? email.trim() : ''
        );
        if (checkKeyRef.current !== currentKey) return; // another change happened while awaiting
        setIsEstablishedPatient(result.found);
      } catch (_) {
        if (checkKeyRef.current === currentKey) {
          setIsEstablishedPatient(false);
        }
      } finally {
        if (checkKeyRef.current === currentKey) setIsCheckingPatient(false);
      }
    }, 700);

    return () => clearTimeout(timer);
  }, [practice, firstName, lastName, cellPhone, email]);

  // Load providers when reason = 'Appointment Request' and a practice is selected
  useEffect(() => {
    if (reason !== 'Appointment Request' || !practice || practices.length === 0) {
      setProviders([]);
      setPreferredProvider('');
      return;
    }
    const selected = practices.find(p => p.practice_name === practice);
    if (!selected?.practice_code) return;
    setLoadingProviders(true);
    getProviders(selected.practice_code)
      .then(data => setProviders(Array.isArray(data) ? data : []))
      .finally(() => setLoadingProviders(false));
  }, [reason, practice, practices]);

  // Update practice color when practice selection changes
  useEffect(() => {
    if (practice && practices.length > 0) {
      const selectedPractice = practices.find(p => p.practice_name === practice);
      if (selectedPractice && selectedPractice.color_main) {
        setPracticeColor(selectedPractice.color_main);
      }
    }
  }, [practice, practices]);

  const reasonOptions = isEstablishedPatient === true
    ? [...ESTABLISHED_ONLY_OPTIONS, 'Other']
    : BASE_REASON_OPTIONS;

  // Clear reason if the selected option is no longer available (e.g. practice changed, patient not found)
  useEffect(() => {
    if (reason && isEstablishedPatient !== true && ESTABLISHED_ONLY_OPTIONS.includes(reason)) {
      setReason('');
    }
  }, [isEstablishedPatient, reason]);

  const validate = () => {
    const errors = {};
    if (!practice) errors.practice = 'Please select a practice';
    if (!firstName.trim()) errors.firstName = 'First name is required';
    if (!lastName.trim()) errors.lastName = 'Last name is required';
    if (!cellPhone || cellPhone.replace(/\D/g, '').length < 10)
      errors.cellPhone = 'A valid 10-digit phone number is required';
    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!reason) errors.reason = 'Please select a reason for contacting';
    if (!message.trim()) errors.message = 'Message is required';
    return errors;
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setCellPhone(digits);
  };

  const handleSubmit = async () => {
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setIsSubmitting(true);
    setError('');

    try {
      const isLocal = window.location.port === '3000';
      const base = process.env.REACT_APP_API_BASE || (isLocal ? 'http://localhost:3001' : '');
      
      const payload = {
        practice,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        cellPhone: formatPhone(cellPhone),
        email: email.trim(),
        reason,
        message: message.trim(),
        source: source || 'direct',
        preferredProvider: preferredProvider || undefined
      };
      
      const res = await fetch(`${base}/api/contact-us`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Server error: ${res.status}`);
      }

      setSubmitSuccess(true);
    } catch (err) {
      console.error('Contact form submission error:', err);
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Message Sent Successfully!
          </Typography>
          <Typography variant="body2" paragraph>
            Thank you for contacting <strong>{practice}</strong>.
          </Typography>
          <Typography variant="body2" paragraph>
            A confirmation has been sent to <strong>{email}</strong>.
          </Typography>
          <Typography variant="body2">
            A member of our team will be in touch with you shortly.
          </Typography>
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button variant="contained" color="primary" href="/">
            Return to Home
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
      {!isEmbedded && (
        <Paper
          sx={{
            p: 3,
            mb: 3,
            background: `linear-gradient(135deg, ${practiceColor} 0%, ${practiceColor}80 100%)`
          }}
        >
          <Typography variant="h4" align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
            Contact Us
          </Typography>
          <Typography variant="body1" align="center" sx={{ color: 'white', mt: 1 }}>
            Fill out the form below and we'll get back to you shortly.
          </Typography>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {!isEmbedded && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: practiceColor }}>
            Select Practice
          </Typography>
          <TextField
            select
            fullWidth
            required
            label="Practice"
            value={practice}
            onChange={(e) => !lockedPractice && setPractice(e.target.value)}
            disabled={practicesLoading || !!lockedPractice}
            error={!!fieldErrors.practice}
            helperText={
              fieldErrors.practice ||
              (lockedPractice ? 'Practice pre-selected from your link' : '')
            }
          >
            {practices.map((p) => (
              <MenuItem key={p.practice_name} value={p.practice_name}>
                {p.practice_name}
              </MenuItem>
            ))}
          </TextField>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: practiceColor }}>
          Your Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              error={!!fieldErrors.firstName}
              helperText={fieldErrors.firstName}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              error={!!fieldErrors.lastName}
              helperText={fieldErrors.lastName}
            />
          </Grid>
          {(isCheckingPatient || isEstablishedPatient !== null) && (
            <Grid item xs={12} sx={{ pt: '4px !important', pb: 0 }}>
              {isCheckingPatient ? (
                <Chip
                  size="small"
                  icon={<CircularProgress size={12} />}
                  label="Checking patient status…"
                  variant="outlined"
                  sx={{ fontSize: '0.72rem', color: 'text.secondary', borderColor: 'divider' }}
                />
              ) : isEstablishedPatient === true ? (
                <Chip
                  size="small"
                  label="✓ Established patient"
                  color="success"
                  variant="outlined"
                  sx={{ fontSize: '0.72rem' }}
                />
              ) : isEstablishedPatient === false ? (
                <Chip
                  size="small"
                  label="New patient"
                  variant="outlined"
                  sx={{ fontSize: '0.72rem', color: 'text.secondary', borderColor: 'divider' }}
                />
              ) : null}
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Cell Phone"
              value={formatPhone(cellPhone)}
              onChange={handlePhoneChange}
              placeholder="555-123-4567"
              inputProps={{ maxLength: 12 }}
              error={!!fieldErrors.cellPhone}
              helperText={fieldErrors.cellPhone}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              type="email"
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              required
              label="Reason for Contacting"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              error={!!fieldErrors.reason}
              helperText={fieldErrors.reason || ''}
              SelectProps={{ renderValue: (val) => val }}
            >
              {reasonOptions.map((opt) => (
                <MenuItem
                  key={opt}
                  value={opt}
                  sx={{
                    whiteSpace: 'normal',
                    alignItems: 'flex-start',
                    ...(ESTABLISHED_ONLY_OPTIONS.includes(opt) ? { fontWeight: 'bold', color: 'primary.main' } : {})
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: ESTABLISHED_ONLY_OPTIONS.includes(opt) ? 'bold' : 'normal' }}>
                      {opt}
                    </Typography>
                    {REASON_DESCRIPTIONS[opt] && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', whiteSpace: 'normal' }}>
                        {REASON_DESCRIPTIONS[opt]}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {reason === 'Appointment Request' && (
            <Grid item xs={12} sm={7} md={6}>
              <TextField
                select
                fullWidth
                label="Preferred Provider (optional)"
                value={preferredProvider}
                onChange={(e) => setPreferredProvider(e.target.value)}
                disabled={loadingProviders}
                helperText={
                  loadingProviders ? 'Loading providers…' :
                  providers.length === 0 ? 'No providers available for this practice' :
                  'Select your preferred provider, or leave blank for no preference'
                }
                SelectProps={{
                  renderValue: (val) => val === '' ? 'No preference' : val
                }}
              >
                <MenuItem value="">No preference</MenuItem>
                {providers.map((p) => (
                  <MenuItem key={p.id} value={p.name}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <span>{p.name}</span>
                      {p.url && (
                        <Tooltip title="View provider profile">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(p.url, '_blank', 'noopener,noreferrer');
                            }}
                            sx={{ p: 0.25, color: practiceColor }}
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              multiline
              rows={5}
              label="Message / Additional Details"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Please describe your request in detail..."
              inputProps={{ maxLength: 2000 }}
              error={!!fieldErrors.message}
              helperText={fieldErrors.message || `${message.length}/2000`}
            />
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={isSubmitting}
          sx={{ 
            minWidth: 200, 
            py: 1.5,
            backgroundColor: practiceColor,
            '&:hover': {
              backgroundColor: `${practiceColor}CC`
            }
          }}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Send Message'}
        </Button>
      </Box>
    </Box>
  );
}
